import { useState } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
    LayoutDashboard, Users, Upload, Bell, ImageIcon, FileText,
    LogOut, Menu, X, Moon, Sun, ScrollText, CalendarDays,
} from "lucide-react";
import { Button } from "./ui/button";
import { motion, AnimatePresence } from "framer-motion";

const adminNav = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-admin-dashboard" },
    { to: "/admin/parents", label: "Parents", icon: Users, testId: "nav-admin-parents" },
    { to: "/admin/bulk-upload", label: "Bulk Upload", icon: Upload, testId: "nav-admin-bulk" },
    { to: "/admin/notifications", label: "Notifications", icon: Bell, testId: "nav-admin-notifications" },
    { to: "/admin/events", label: "Events", icon: CalendarDays, testId: "nav-admin-events" },
    { to: "/admin/photos", label: "Photos", icon: ImageIcon, testId: "nav-admin-photos" },
    { to: "/admin/documents", label: "Documents", icon: FileText, testId: "nav-admin-documents" },
    { to: "/admin/daily-digest", label: "Daily Digest", icon: Sun, testId: "nav-admin-digest" },
    { to: "/admin/audit-logs", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
];

const parentNav = [
    { to: "/parent", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-parent-dashboard" },
    { to: "/parent/notifications", label: "Notifications", icon: Bell, testId: "nav-parent-notifications" },
    { to: "/parent/events", label: "Events", icon: CalendarDays, testId: "nav-parent-events" },
    { to: "/parent/photos", label: "Photos", icon: ImageIcon, testId: "nav-parent-photos" },
    { to: "/parent/documents", label: "Documents", icon: FileText, testId: "nav-parent-documents" },
];

const LOGO_URL = "https://childhood.com.co/wp-content/uploads/2018/09/childhood_new_logo.png";

export default function Layout() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);

    const links = user?.role === "admin" ? adminNav : parentNav;

    const handleLogout = () => {
        logout();
        nav("/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Top bar */}
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
                <div className="flex items-center justify-between px-4 md:px-6 h-16">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setOpen((s) => !s)}
                            data-testid="mobile-menu-toggle"
                            aria-label="Toggle menu"
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                        <Link to={user?.role === "admin" ? "/admin" : "/parent"} className="flex items-center gap-2" data-testid="brand-home-link">
                            <img src={LOGO_URL} alt="Childhood" className="h-10 w-auto" />
                            <div className="hidden sm:block leading-tight">
                                <div className="text-sm font-bold font-heading">Childhood</div>
                                <div className="text-xs text-muted-foreground -mt-0.5">Cuddle &amp; Care Portal</div>
                            </div>
                        </Link>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="hidden sm:inline text-sm text-muted-foreground" data-testid="user-greeting">
                            {user?.role === "admin" ? `Hi, ${user?.username}` : `Hi, ${user?.name}`}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggle}
                            data-testid="theme-toggle-btn"
                            aria-label="Toggle dark mode"
                        >
                            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleLogout}
                            className="rounded-full"
                            data-testid="logout-btn"
                        >
                            <LogOut className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Logout</span>
                        </Button>
                    </div>
                </div>
            </header>

            <div className="flex">
                {/* Desktop sidebar */}
                <aside className="hidden md:flex md:flex-col w-60 shrink-0 border-r border-border min-h-[calc(100vh-4rem)] p-4 gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={l.testId}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all ${
                                    isActive
                                        ? "bg-secondary text-secondary-foreground shadow-sm"
                                        : "text-foreground hover:bg-muted"
                                }`
                            }
                        >
                            <l.icon className="h-4 w-4" />
                            {l.label}
                        </NavLink>
                    ))}
                </aside>

                {/* Mobile sidebar (drawer) */}
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="md:hidden fixed inset-0 z-30 bg-black/40"
                            onClick={() => setOpen(false)}
                        >
                            <motion.aside
                                initial={{ x: -260 }}
                                animate={{ x: 0 }}
                                exit={{ x: -260 }}
                                transition={{ type: "tween", duration: 0.2 }}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute left-0 top-16 bottom-0 w-64 bg-background border-r border-border p-4 flex flex-col gap-1"
                            >
                                {links.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        end={l.end}
                                        onClick={() => setOpen(false)}
                                        data-testid={`mobile-${l.testId}`}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all ${
                                                isActive
                                                    ? "bg-secondary text-secondary-foreground"
                                                    : "text-foreground hover:bg-muted"
                                            }`
                                        }
                                    >
                                        <l.icon className="h-4 w-4" />
                                        {l.label}
                                    </NavLink>
                                ))}
                            </motion.aside>
                        </motion.div>
                    )}
                </AnimatePresence>

                <main className="flex-1 min-w-0 p-4 md:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
